import styled from '@emotion/styled'

import { colors, spacing, typography } from '../constants'
import { Text } from '../typography'

interface IContainerProps {
  disabled: boolean
  focused: boolean
  invalid: boolean
  roundedCorners: boolean
}

function pickBorderRadius(roundedCorners = false): string {
  if (roundedCorners) return `${spacing.majorScale(1)}px`
  return '3px'
}

const height = '55px'

export const Container = styled(Text.withComponent('div'))<IContainerProps>`
  border: 1px solid;
  border-radius: ${({ roundedCorners }) => pickBorderRadius(roundedCorners)};
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  height: ${height};
  justify-content: flex-end;
  position: relative;
  width: 100%;

  border-color: ${({ disabled, focused, invalid }) => {
    if (disabled) return colors.border.disabled
    if (focused) return colors.border.active
    if (invalid) return colors.border.error

    return colors.border.default
  }};
`

export const InnerContainer = styled.div`
  box-sizing: border-box;
  display: flex;
  flex-direction: row;
  height: 100%;
  justify-content: space-between;
  left: 0;
  padding-left: ${spacing.majorScale(2)}px;
  padding-right: ${spacing.majorScale(2)}px;
  pointer-events: none;
  position: absolute;
  top: 0;
  width: 100%;
`

export const LabelContainer = styled.div`
  display: flex;
  flex-direction: column;
`

interface IUpperLabelProps {
  active: boolean
  invalid: boolean
}

export const UpperLabel = styled.label<IUpperLabelProps>`
  color: ${({ invalid }) => (invalid ? colors.text.error : colors.text.hint)};
  cursor: text;
  font-size: ${typography.fontSize.label.textField};
  line-height: 28px;

  display: ${({ active, invalid }) => {
    return active || invalid ? 'inherit' : 'none'
  }};

  // HACK (alex): cross-bundle compatibility
  font-weight: unset;
  text-transform: none;
`

interface IMainLabelProps {
  active: boolean
  disabled: boolean
  invalid: boolean
}

export const MainLabel = styled.label<IMainLabelProps>`
  line-height: 18px;
  margin: ${({ active, invalid }) => (active || invalid ? '0' : 'auto')};

  color: ${({ active, disabled }) => {
    if (disabled) return colors.text.disabled
    if (active) return colors.text.default

    return colors.text.hint
  }};

  display: ${({ active }) => {
    return active ? 'none' : 'inherit'
  }};

  // HACK (alex): cross-bundle compatibility
  font-size: unset;
  font-weight: unset;
  text-transform: none;
`

interface IIconContainerProps {
  disabled: boolean
}

export const IconContainer = styled.div<IIconContainerProps>`
  color: ${({ disabled }) => (disabled ? colors.text.disabled : colors.text.hint)};
  margin: auto 0;
  width: 12px;
`

interface IStyledSelectProps {
  active: boolean
  disabled: boolean
  invalid: boolean
  hasLabel: boolean
}

export const StyledSelect = styled.select<IStyledSelectProps>`
  appearance: none;
  background-color: transparent;
  border: none;
  box-shadow: none;
  color: ${({ active, disabled }) => {
    if (disabled) return colors.text.disabled
    if (active) return colors.text.default

    return colors.text.hint
  }};
  height: ${height};
  line-height: 22px;
  margin-left: ${spacing.majorScale(2)}px;
  margin-top: ${({ active, hasLabel, invalid }) =>
    hasLabel && (active || invalid) ? '18px' : '0'};
  outline-style: none;

  &:-moz-focusring {
    color: transparent;
    text-shadow: 0 0 0 #000;
  }

  // HACK: Workaround left padding that Firefox applies to select elements.
  @-moz-document url-prefix() {
    text-indent: -2px;
  }

  // HACK (alex): cross-bundle compatibility
  background: none;
  margin-bottom: 0;
  padding: 0;
  &:focus {
    background: none;
    border: none;
    outline: none;
  }
`

export const ValidationHint = styled(Text)`
  color: ${colors.text.error};
  margin-top: ${spacing.majorScale(1)}px;
`
